/**
 * manager-header.js
 * Gerencia dropdowns de navegação desktop com hover/focus e delay.
 * O drawer mobile é gerenciado pelo DrawerManager (data-drawer="menu").
 * Disponível como window.HeaderManager.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <!-- Container (escopo dos triggers) -->
 *   <nav data-header>
 *
 *     <!-- Trigger: valor deve corresponder ao data-dropdown-menu -->
 *     <a href="/colecoes" data-dropdown-trigger="roupas">Roupas</a>
 *
 *   </nav>
 *
 *   <!-- Menu dropdown (em qualquer lugar do DOM) -->
 *   <div data-dropdown-menu="roupas" aria-hidden="true">
 *     <!-- Elemento interno: hover aqui mantém o menu aberto -->
 *     <div data-dropdown-content>
 *       ...
 *     </div>
 *   </div>
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   window.HeaderManager.closeDropdowns()
 *   window.HeaderManager.destroy()
 *
 * ─── Eventos emitidos via window.eventBus ─────────────────────────────────
 *
 *   'dropdown:open'  → { name, menu, trigger }
 *   'dropdown:close' → { name, menu }
 */

class HeaderManager {
  constructor(options = {}) {
    this._config = {
      containerSelector: '[data-header]',
      triggerSelector:   '[data-dropdown-trigger]',
      menuSelector:      '[data-dropdown-menu]',
      openDelay:  150,
      closeDelay: 200,
      openClass:  'is-open',
      activeClass: 'is-active',
      ...options,
    };

    /** @type {Map<string, ReturnType<typeof setTimeout>>} */
    this._timers = new Map();
    this._activeMenu = null;

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick   = this._onClick.bind(this);

    this._init();
    this._bindGlobalEvents();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    const container = document.querySelector(this._config.containerSelector);
    if (!container) return;

    container.querySelectorAll(this._config.triggerSelector).forEach(trigger => {
      const name = trigger.dataset.dropdownTrigger;
      const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
      if (!menu) return;

      // Hover no trigger
      trigger.addEventListener('mouseenter', () => this._scheduleOpen(name));
      trigger.addEventListener('mouseleave', () => this._scheduleClose(name));

      // Hover na área interna (data-dropdown-content) mantém o menu aberto;
      // sem ele, o hover é mantido no menu inteiro.
      const hoverTarget = menu.querySelector('[data-dropdown-content]') || menu;
      hoverTarget.addEventListener('mouseenter', () => this._cancelClose(name));
      hoverTarget.addEventListener('mouseleave', () => this._scheduleClose(name));

      // Suporte a teclado (focus/blur)
      trigger.addEventListener('focus', () => this._scheduleOpen(name));
      trigger.addEventListener('blur',  () => this._scheduleClose(name));
    });

    // Fecha dropdowns ao abrir qualquer drawer
    window.eventBus?.on('drawer:open', () => this.closeDropdowns());
  }

  _bindGlobalEvents() {
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click',   this._onClick);
  }

  // ─── Eventos globais ──────────────────────────────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'Escape') this.closeDropdowns();
  }

  _onClick(e) {
    // Fecha ao clicar fora de triggers e menus
    if (
      !e.target.closest(this._config.triggerSelector) &&
      !e.target.closest(this._config.menuSelector)
    ) {
      this.closeDropdowns();
    }
  }

  // ─── Timers de hover ──────────────────────────────────────────────────────

  _scheduleOpen(name) {
    this._cancelClose(name);
    const timer = setTimeout(() => this._open(name), this._config.openDelay);
    this._timers.set(`open:${name}`, timer);
  }

  _scheduleClose(name) {
    this._cancelOpen(name);
    const timer = setTimeout(() => this._close(name), this._config.closeDelay);
    this._timers.set(`close:${name}`, timer);
  }

  _cancelOpen(name) {
    clearTimeout(this._timers.get(`open:${name}`));
    this._timers.delete(`open:${name}`);
  }

  _cancelClose(name) {
    clearTimeout(this._timers.get(`close:${name}`));
    this._timers.delete(`close:${name}`);
  }

  // ─── Abrir / fechar ───────────────────────────────────────────────────────

  _open(name) {
    if (this._activeMenu && this._activeMenu !== name) {
      this._close(this._activeMenu);
    }

    const menu    = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.add(this._config.openClass);
    menu.removeAttribute('aria-hidden');
    trigger?.classList.add(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'true');
    this._activeMenu = name;

    window.eventBus?.emit('dropdown:open', { name, menu, trigger });
  }

  _close(name) {
    const menu    = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.remove(this._config.openClass);
    menu.setAttribute('aria-hidden', 'true');
    trigger?.classList.remove(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'false');

    if (this._activeMenu === name) this._activeMenu = null;

    window.eventBus?.emit('dropdown:close', { name, menu });
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  closeDropdowns() {
    if (this._activeMenu) this._close(this._activeMenu);
  }

  destroy() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click',   this._onClick);
    this._timers.forEach(t => clearTimeout(t));
    this._timers.clear();
  }
}

window.HeaderManager = new HeaderManager();
